import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '@afosto/app-components-react/Form';
import Autocomplete from '@afosto/app-components-react/Autocomplete';
import { handling as handlingApi } from '@afosto/fulfilment/api/Handling';
import { index as searchApi } from '@afosto/content/api/Index';
import translations from './translations';

const HandlingListSearch = ({
  inputProps,
  name,
  onChange,
  validateHandlingList,
  enableCreate,
  ...props
}) => {
  const intl = useIntl();
  const [isFetchingHandlingLists, setIsFetchingHandlingLists] = useState(false);
  const [handlingListSuggestions, setHandlingListSuggestions] = useState([]);
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  /**
   * Search for handling lists
   * @param {string} searchValue
   * @returns {Promise}
   */
  const searchHandlingLists = useCallback(async searchValue => {
    try {
      setIsFetchingHandlingLists(true);

      const response = await searchApi.search({
        codes: ['lcs'],
        models: ['lists'],
        namespaces: ['default'],
        q: searchValue?.length ? [searchValue] : null,
      });
      const { data = {} } = response;
      const { hits = [] } = data;
      const suggestions = hits.reduce((acc, hit) => {
        const { title, metadata = {}, reference } = hit;
        return [
          ...acc,
          {
            ...metadata,
            number: title,
            label: title,
            value: reference,
            id: reference,
          },
        ];
      }, []);
      setHandlingListSuggestions(suggestions);
    } catch (err) {
      setHandlingListSuggestions([]);
    } finally {
      setIsFetchingHandlingLists(false);
    }
  }, []);

  useEffect(() => {
    searchHandlingLists();
  }, [searchHandlingLists]);

  /**
   * Search handling lists based on the input value.
   * @param {Event} event
   */
  const handleSearchHandlingList = event => {
    const { value } = event?.target ?? {};
    searchHandlingLists(value).catch(() => {
      // Do something with error.
    });
  };

  /**
   * Set the handling list value on the form or trigger the provided onChange callback
   * @param {object|Event} ev
   * @param {object|null} handlingList
   */
  const setHandlingListValue = (ev, handlingList) => {
    if (formContext && !onChange) {
      setValue(name, handlingList);
      return;
    }

    if (onChange) {
      onChange(ev, handlingList);
    }
  };

  const fetchHandlingLists = async ids => {
    try {
      const promises = ids.map(id => handlingApi.viewHandlingList(id));
      const responses = await Promise.allSettled(promises).then(result =>
        result.filter(promise => promise.status === 'fulfilled'),
      );

      return responses.reduce((acc, response) => {
        if (response?.value?.data) {
          return [...acc, response.value.data];
        }
        return acc;
      }, []);
    } catch (err) {
      return [];
    }
  };

  /**
   * Handle the change on the shipment search.
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = async (ev, newValue) => {
    try {
      const shouldValidateHandlingList = validateHandlingList && !enableCreate;

      if (!shouldValidateHandlingList) {
        setHandlingListValue(ev, newValue ?? null);
        return;
      }

      if (newValue) {
        const handlingLists = await fetchHandlingLists(
          Array.isArray(newValue) ? newValue.map(item => item.id) : [newValue?.id],
        );
        const handlingListValues = handlingLists.reduce(
          (acc, handlingList) => [
            ...acc,
            { ...handlingList, label: handlingList.number, value: handlingList.id },
          ],
          [],
        );

        if (Array.isArray(newValue)) {
          setHandlingListValue(ev, handlingListValues);
        } else {
          const [firstHandlingList] = handlingListValues;
          setHandlingListValue(ev, firstHandlingList);
        }
      } else {
        setHandlingListValue(ev, null);
      }
    } catch (err) {
      setHandlingListValue(ev, null);
    }
  };

  return (
    <Autocomplete
      {...props}
      name={name}
      onChange={handleOnChange}
      onInputChange={handleSearchHandlingList}
      options={handlingListSuggestions}
      loading={isFetchingHandlingLists}
      enableCreate={enableCreate}
      inputProps={{
        ...(inputProps ?? {}),
        placeholder: intl.formatMessage(translations.fields.handlingList.placeholder),
      }}
    />
  );
};

HandlingListSearch.propTypes = {
  ...Autocomplete.propTypes,
  validateHandlingList: PropTypes.bool,
};

HandlingListSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  validateHandlingList: true,
};

export default HandlingListSearch;
